/**
 * HrV1CycleIndex.tsx
 *
 * Lists every hr_v1 cycle in the project with a one-row health snapshot:
 * step, EPR count, 360 count, appraisals, open appeals, outcomes. From
 * each row HR can open the per-cycle console or reset dirty data.
 *
 * Route: /hr-v1 (HR-role only)
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { routes } from '../routes';
import Button from '../components/ui/Button';
import CenterMessage from '../components/ui/CenterMessage';
import DisclosureBadge from '../components/epr/DisclosureBadge';
import WorkflowStepper, { type WorkflowStep } from '../components/epr/WorkflowStepper';
import DistributionPackage, { type DistributionLink } from '../components/epr/DistributionPackage';
import { useToast } from '../components/ui/Toast';
import {
  snapshotAllHrV1Cycles, type HrV1CycleSnapshot,
} from '../services/hrV1CycleService';
import { resetCycleData } from '../services/hrCycleReset';
import { createDemoCycle, type DemoSeedResult } from '../services/hrDemoSeed';
import { useAuth } from '../contexts/AuthContext';

const HrV1CycleIndex: React.FC = () => {
  const toast = useToast();
  const { user } = useAuth();
  const [rows, setRows] = useState<HrV1CycleSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<DemoSeedResult | null>(null);

  const handleCreateDemo = useCallback(async () => {
    setSeeding(true);
    try {
      const res = await createDemoCycle({ createdBy: user?.uid ?? 'unknown' });
      setSeedResult(res);
      toast.show(`Demo cycle dibuat: ${res.cycleName}`, { icon: 'check' });
      setRefreshTick(t => t + 1);
    } catch (e) {
      toast.show(`Seed failed: ${(e as Error).message}`, { icon: 'error' });
    } finally { setSeeding(false); }
  }, [user?.uid, toast]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    snapshotAllHrV1Cycles()
      .then(r => { if (alive) setRows(r); })
      .catch(e => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [refreshTick]);

  const handleReset = useCallback(async (cycleId: string, cycleName: string) => {
    const confirmed = window.confirm(
      `Reset SEMUA data hr_v1 untuk cycle "${cycleName}"?\n\n` +
      `Akan menghapus permanen: EPR submission, 360 submission, magic-link token, ` +
      `calibration, appraisal, appeal, outcome. ReviewCycle akan di-reset ke draft.\n\n` +
      `OK = lanjut, Cancel = batal.`,
    );
    if (!confirmed) return;
    setResettingId(cycleId);
    try {
      const res = await resetCycleData(cycleId);
      toast.show(`Reset selesai. ${res.totalDeleted} doc dihapus.`, { icon: 'delete' });
      setRefreshTick(t => t + 1);
    } catch (e) {
      toast.show(`Reset gagal: ${(e as Error).message}`, { icon: 'error' });
    } finally { setResettingId(null); }
  }, [toast]);

  if (loading) return <CenterMessage tone="info" title="Loading hr_v1 cycles..." />;
  if (error) {
    return (
      <CenterMessage
        tone="error"
        title="Cannot load hr_v1 cycles"
        body={error}
      />
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500">HR Console</div>
          <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">Performance cycles</h1>
          <p className="text-sm text-stone-600 mt-1">
            {rows.length} hr_v1 cycle{rows.length !== 1 ? 's' : ''} in this project
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="secondary"
            icon="science"
            loading={seeding}
            onClick={handleCreateDemo}
          >
            Create demo cycle
          </Button>
          <Button
            variant="secondary"
            icon="refresh"
            onClick={() => setRefreshTick(t => t + 1)}
          >
            Refresh
          </Button>
        </div>
      </header>

      {seedResult && (
        <SeedResultBanner result={seedResult} onClose={() => setSeedResult(null)} />
      )}

      {rows.length === 0 ? (
        <CenterMessage
          tone="info"
          title="No hr_v1 cycles yet"
          body="Buat ReviewCycle di Firestore dengan frameworkVersion='hr_v1' lalu buka /hr-v1/<cycleId>."
        />
      ) : (
        <div className="space-y-3">
          {rows.map(snap => (
            <CycleRow
              key={snap.cycle.id}
              snap={snap}
              busy={resettingId === snap.cycle.id}
              onReset={() => handleReset(snap.cycle.id, snap.cycle.name)}
            />
          ))}
        </div>
      )}

      <DocsBanner />
    </div>
  );
};

// ─── Row ────────────────────────────────────────────────────────────

const CycleRow: React.FC<{
  snap: HrV1CycleSnapshot;
  busy: boolean;
  onReset: () => void;
}> = ({ snap, busy, onReset }) => {
  const c = snap.cycle;
  const counts = snap.counts;
  const dirty = counts.eprSubmissions + counts.review360Submissions
              + counts.appraisalForms + counts.outcomeRecords > 0;

  return (
    <section className="rounded-xl border border-stone-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-semibold text-stone-900">{c.name}</h2>
            <DisclosureBadge
              icon="hub"
              label={`Step ${c.currentStep ?? '—'}/7`}
              tone={c.currentStep ? 'info' : 'neutral'}
            />
            <DisclosureBadge
              icon={c.status === 'closed' ? 'archive' : c.status === 'active' ? 'play_circle' : 'edit'}
              label={`status: ${c.status}`}
              tone={c.status === 'closed' ? 'success' : c.status === 'active' ? 'info' : 'neutral'}
            />
            {dirty && (
              <DisclosureBadge icon="storage" label="ada data" tone="warning" />
            )}
          </div>
          <div className="text-xs text-stone-500 mt-1">
            <code>{c.id}</code> · period {c.period} {c.year}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={routes.hrV1Cycle(c.id)}>
            <Button variant="primary" icon="open_in_new">Open console</Button>
          </Link>
          <Button
            variant="secondary"
            icon="delete"
            loading={busy}
            disabled={!dirty || c.status === 'closed'}
            onClick={onReset}
          >
            Reset
          </Button>
        </div>
      </div>

      <WorkflowStepper steps={compactStepsFromSnapshot(snap)} compact />
      <div className="text-[10px] text-stone-500 mt-1">
        {counts.eprSubmissions} EPR · {counts.review360Submissions} 360 · {counts.appraisalForms} appraisal
        {counts.openAppeals > 0 && (
          <span className="ml-2 text-amber-700 font-medium">· {counts.openAppeals} open appeals</span>
        )}
      </div>
    </section>
  );
};

// Compact 7-step view from a cycle snapshot — used in list rows
function compactStepsFromSnapshot(snap: HrV1CycleSnapshot): WorkflowStep[] {
  const c = snap.cycle;
  const counts = snap.counts;
  const cur = c.currentStep ?? 0;
  return [1, 2, 3, 4, 5, 6, 7].map(num => {
    let status: WorkflowStep['status'];
    if (num < cur) status = 'done';
    else if (num === cur) status = 'active';
    else status = 'pending';
    // Override based on data hints
    if (num === 1 && counts.eprSubmissions === 0 && cur === 0) status = 'pending';
    if (num === 3 && counts.appraisalForms > 0) status = num < cur ? 'done' : status;
    if (num === 4 && snap.hasCalibrationSession === false && cur < 4) status = 'pending';
    if (num === 6 && counts.openAppeals === 0 && cur >= 6) status = 'skipped';
    if (num === 7 && counts.outcomeRecords > 0 && counts.outcomeRecords < counts.appraisalForms) status = 'active';
    if (num === 7 && counts.appraisalForms > 0 && counts.outcomeRecords === counts.appraisalForms) status = 'done';
    return {
      num,
      label: '',
      status,
    };
  });
}

const SeedResultBanner: React.FC<{
  result: DemoSeedResult;
  onClose: () => void;
}> = ({ result, onClose }) => {
  const links: DistributionLink[] = [
    ...result.selfUrls.map(u => ({
      recipientEmail: `${u.employeeId}@demo.local`,
      recipientName:  u.employeeId,
      action:         'EPR self-assessment',
      url:            u.url,
    })),
    ...result.managerUrls.map(u => ({
      recipientEmail: `${u.employeeId}-mgr@demo.local`,
      recipientName:  `Manager of ${u.employeeId}`,
      action:         'EPR manager review',
      context:        `reviewing ${u.employeeId}`,
      url:            u.url,
    })),
    ...result.reviewer360Urls.map(u => ({
      recipientEmail: u.reviewerEmail,
      action:         '360 feedback',
      context:        `for ${u.revieweeId} (${u.relationship})`,
      url:            u.url,
    })),
  ];

  return (
    <section className="rounded-xl border border-stone-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-stone-900">Demo cycle ready</h2>
          <div className="text-xs text-stone-500 mt-0.5">
            {result.cycleName} · <code>{result.cycleId}</code>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={routes.hrV1Cycle(result.cycleId)}>
            <Button variant="primary" icon="open_in_new">Open console</Button>
          </Link>
          <Button variant="tertiary" icon="close" onClick={onClose}>Close</Button>
        </div>
      </div>
      <DistributionPackage
        title="Demo cycle links"
        cycleName={result.cycleName}
        links={links}
      />
    </section>
  );
};

const DocsBanner: React.FC = () => (
  <section className="rounded-xl border border-stone-200 bg-stone-50 p-5">
    <h3 className="text-sm font-semibold text-stone-800 mb-2">Workflow testing</h3>
    <p className="text-sm text-stone-600 leading-relaxed">
      Before running a test scenario, make sure the target cycle is clean
      (click <b>Reset</b> on the row — disabled when the cycle has no
      hr_v1 data). The full scenarios with JSON input live at
      <code className="text-xs"> docs/HR_V1_TEST_SCENARIOS.md</code>.
    </p>
  </section>
);

export default HrV1CycleIndex;
